import { Grid } from "@material-ui/core";
import AppHeader from "@saleor/components/AppHeader";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { CurrencyrateDetailsFragment } from "@saleor/fragments/types/CurrencyrateDetailsFragment";
import { CurrencyrateErrorFragment } from "@saleor/fragments/types/CurrencyrateErrorFragment";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ConversionMethodEnum } from "@saleor/types/globalTypes";
import { concat } from "lodash-es";
import React from "react";
import { useIntl } from "react-intl";

import CurrencyrateDetails from "../CurrencyrateDetails/CurrencyrateDetails";
import CurrencyrateProperties from "../CurrencyrateProperties";

export interface CurrencyratePageFormData {
  code: string;
  name: string;
  symbol: string;
  isPrimaryStoreCurrency: boolean;
  isPrimaryExchangeRateCurrency: boolean;
  isPublished: boolean;
  displayOrder: number;
  displayFormat: string;
  rate: number;
  conversionMethod: ConversionMethodEnum;
}

export interface CurrencyratePageProps {
  currencyrate: CurrencyrateDetailsFragment | null;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onDelete: () => void;
  onSubmit: (data: CurrencyratePageFormData) => void;
  errors: CurrencyrateErrorFragment[];
  title: string;
}

const CurrencyratePage: React.FC<CurrencyratePageProps> = ({
  currencyrate,
  disabled,
  errors,
  saveButtonBarState,
  onBack,
  onDelete,
  onSubmit,
  title
}) => {
  const intl = useIntl();
  const onlineRate = maybe(() => currencyrate.slug.rate, 1);
  const initialForm: CurrencyratePageFormData =
    currencyrate === null
      ? {
          code: "",
          conversionMethod: ConversionMethodEnum.METHODONE,
          displayFormat: "USD $ 0.00 ",
          displayOrder: 1,
          isPrimaryExchangeRateCurrency: false,
          isPrimaryStoreCurrency: false,
          isPublished: false,
          name: "",
          rate: 1,
          symbol: ""
        }
      : {
          code: maybe(() => currencyrate.code, "USD"),
          conversionMethod: maybe(
            () => currencyrate.conversionMethod,
            ConversionMethodEnum.METHODONE
          ),
          displayFormat: maybe(() => currencyrate.displayFormat, "USD $ 0.00 "),
          displayOrder: maybe(() => currencyrate.displayOrder, 1),
          isPrimaryExchangeRateCurrency: maybe(
            () => currencyrate.isPrimaryExchangeRateCurrency,
            true
          ),
          isPrimaryStoreCurrency: maybe(
            () => currencyrate.isPrimaryStoreCurrency,
            true
          ),
          isPublished: maybe(() => currencyrate.isPublished, true),
          name: maybe(() => currencyrate.name, ""),

          rate: maybe(() => currencyrate.rate, 1),

          symbol: maybe(() => currencyrate.symbol, "")
        };

  const handleSubmit = (data: CurrencyratePageFormData) =>
    onSubmit({
      ...data
    });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.currencyrates)}
          </AppHeader>
          <PageHeader
            title={
              currencyrate === null
                ? intl.formatMessage({
                    defaultMessage: "Create New Currencyrate",
                    description: "page title"
                  })
                : maybe(() => concat(currencyrate.name, " Rate"))
            }
          />
          <Grid>
            <div>
              <CurrencyrateDetails
                errors={errors}
                onlineRate={onlineRate}
                data={data}
                disabled={disabled}
                onChange={change}
                title={title}
              />
            </div>
            <FormSpacer />
            <div>
              <CurrencyrateProperties
                data={data}
                disabled={disabled}
                onChange={change}
              />
            </div>
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            state={saveButtonBarState}
            onCancel={onBack}
            onSave={submit}
            onDelete={currencyrate === null ? undefined : onDelete}
          />
        </Container>
      )}
    </Form>
  );
};
export default CurrencyratePage;
